import type { Dnd5eAbilityScoreTag } from "@/lib/rules/dnd5e/vocabulary/ability-score";

export const STANDARD_ARRAY = [15, 14, 13, 12, 10, 8] as const;
export const ABILITIES: readonly Dnd5eAbilityScoreTag[] = [
  "str",
  "dex",
  "con",
  "int",
  "wis",
  "cha",
];

export const POINT_BUY_BUDGET = 27;
export const POINT_BUY_MIN = 8;
export const POINT_BUY_MAX = 15;

// SRD 5.2.1 point-buy cost table
const POINT_BUY_COSTS: Record<number, number> = {
  8: 0,
  9: 1,
  10: 2,
  11: 3,
  12: 4,
  13: 5,
  14: 7,
  15: 9,
};

export function pointBuyCost(score: number): number {
  if (Number.isNaN(score) || score < POINT_BUY_MIN || score > POINT_BUY_MAX) {
    return Number.POSITIVE_INFINITY;
  }
  return POINT_BUY_COSTS[score] ?? 0;
}

export function pointsSpent(
  scores: Record<Dnd5eAbilityScoreTag, number>
): number {
  return ABILITIES.reduce((sum, a) => sum + pointBuyCost(scores[a]), 0);
}

export function defaultPointBuyScores(): Record<Dnd5eAbilityScoreTag, number> {
  return Object.fromEntries(ABILITIES.map((a) => [a, POINT_BUY_MIN])) as Record<
    Dnd5eAbilityScoreTag,
    number
  >;
}

export function isValidPointBuy(
  scores: Record<Dnd5eAbilityScoreTag, number>
): boolean {
  return (
    pointsSpent(scores) <= POINT_BUY_BUDGET &&
    ABILITIES.every(
      (a) => scores[a] >= POINT_BUY_MIN && scores[a] <= POINT_BUY_MAX
    )
  );
}

/** Set an ability score, clamped to [POINT_BUY_MIN, POINT_BUY_MAX] and the remaining budget. */
export function setAbilityScore(
  scores: Record<Dnd5eAbilityScoreTag, number>,
  ability: Dnd5eAbilityScoreTag,
  value: number
): Record<Dnd5eAbilityScoreTag, number> {
  const clamped = Math.max(POINT_BUY_MIN, Math.min(POINT_BUY_MAX, value));
  const next = { ...scores, [ability]: clamped };
  if (pointsSpent(next) > POINT_BUY_BUDGET) {
    return scores;
  }
  return next;
}

/** The six standard-array values are a valid assignment iff each is used once. */
export function isValidAssignment(
  scores: Record<Dnd5eAbilityScoreTag, number>
): boolean {
  const values = ABILITIES.map((a) => scores[a]).sort((x, y) => y - x);
  return (
    values.length === STANDARD_ARRAY.length &&
    values.every((v, i) => v === STANDARD_ARRAY[i])
  );
}

/** Swap the values of two abilities (the only mutation the UI offers). */
export function swapAbilities(
  scores: Record<Dnd5eAbilityScoreTag, number>,
  a: Dnd5eAbilityScoreTag,
  b: Dnd5eAbilityScoreTag
): Record<Dnd5eAbilityScoreTag, number> {
  return { ...scores, [a]: scores[b], [b]: scores[a] };
}
